import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { DataManager } from '@syncfusion/ej2-data';
import { GridModel, PageSettingsModel, TextWrapSettingsModel } from '@syncfusion/ej2-grids';
import {
  ApiGridParameters, CrawfordApiGridAdaptor, CrawfordDownloadService, CrawfordGridComponent,
  CrawfordSearchBoxComponent, SearchOperatorsConfig, CrawfordSnackbarService, CrawfordSpinnerService, DateFormat,
  GridColumn, IPageTitle
} from 'crawford-shell';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/zip';
import { global } from '../../app.settings';
import { SavedSearch } from '../../saved-search/model/saved-search.model';
import { SaveSearchDialogComponent } from '../../saved-search/save-search-dialog/save-search-dialog.component';
import { SavedSearchService } from '../../saved-search/saved-search.service';
import { UserDetails } from '../../user/user-details.model';
import { UserService } from '../../user/user.service';
import { ClaimService } from '../claim.service';
import { SearchFilterService } from './services/search-filter.service';
import { AdvancedSearch } from '../model/advanced-search.model';
import { AssociatedClaim } from '../model/associated-claims.model';
import { ClaimSearchCriteria } from '../model/claim-search-criteria.model';
import { ClaimSearchResult } from '../model/claim-search-result.model';
import { SimpleSearch } from '../model/simple-search.model';
import { ClaimInsurerSearchComponent } from './claim-insurer-search/claim-insurer-search.component';
import { ClaimRefSearchComponent } from './claim-ref-search/claim-ref-search.component';
import { ClaimStatusSearchComponent } from './claim-status-search/claim-status-search.component';
import { AccordionService } from './services/accordion.service';
import { ClaimHandlerSearchComponent } from './claim-handler-search/claim-handler-search.component';
import { ClaimSourceSearchComponent } from './claim-source-search/claim-source-search.component';

@Component({
  selector: 'app-claim-list',
  templateUrl: './claim-list.component.html',
  styleUrls: ['./claim-list.component.scss']
})
export class ClaimListComponent implements OnInit, AfterViewInit {

  @ViewChild('claimRefSearch') protected claimRefSearch: ClaimRefSearchComponent;
  @ViewChild('insurerSearch') protected insurerSearch: ClaimInsurerSearchComponent;
  @ViewChild('statusSearch') protected statusSearch: ClaimStatusSearchComponent;
  @ViewChild('handlerSearch') protected handlerSearch: ClaimHandlerSearchComponent;
  @ViewChild('claimSource') protected sourceSearch: ClaimSourceSearchComponent;

  @ViewChild('ccogrid') public ccoGrid: CrawfordGridComponent;
  @ViewChild('simpleSearchBox') protected simpleSearchBox: CrawfordSearchBoxComponent;
  @ViewChild('saveSearchDialog') private saveSearchDialog: SaveSearchDialogComponent;
  @ViewChild('gridStart') private gridStart: ElementRef;

  public adaptor: CrawfordApiGridAdaptor;
  public dataSource: DataManager;
  public advancedSearchGroup: FormGroup;
  public formBuilder: FormBuilder;
  public advancedSearchCriteria: ClaimSearchCriteria;
  public simpleSearchGroup: FormGroup;
  public simpleSearchOperatorsConfig: SearchOperatorsConfig;
  public advSearchOperatorsConfig: SearchOperatorsConfig;
  public readonly references;
  public readonly names;
  protected exportIsAvailable = false;
  public rootAccordionHasChanges = false;
  public accordionChanges: any;

  public showExportButton: boolean;
  public showGrid: boolean;
  public gridData: ClaimSearchResult[];
  public linkedFilesData: AssociatedClaim[] = new Array<AssociatedClaim>();
  public linkedFilesQueryString = 'crawfordRef';
  public crawfordRefs: string[] = new Array<string>();
  public rowDetails: Map<string, object> = new Map<string, object>();
  public http: HttpClient;

  public defaultAdvancedSearch: AdvancedSearch;
  public savedSearch: SavedSearch;


  sortingSettings: any = {
    columns: [{ field: 'instructionDate', direction: 'descending' }]
  };

  df: DateFormat = {
    type: 'date',
    format: global.dateFormat
  };

  displayColumns: GridColumn[] = [
    new GridColumn('crawfordRef', 'Crawford Reference'),
    new GridColumn('policyholder', 'Policyholder'),
    new GridColumn('policyholderPostcode', 'Policyholder Postcode'),
    new GridColumn('lossPostcode', 'Loss Postcode'),
    new GridColumn('insurerRef', 'Insurer Reference', { visible: false }),
    new GridColumn('policyNumber', 'Policy Number', { visible: false }),
    new GridColumn('lossDate', 'Loss Date', { type: 'date', format: this.df }),
    new GridColumn('perilDescription', 'Peril Description', { visible: false }),
    new GridColumn('instructionDate', 'Instruction Date', { type: 'date', format: this.df }),
    new GridColumn('status', 'Current Status'),
    new GridColumn('insurer', 'Insurer')
  ];

  gridPageSettings: PageSettingsModel = {
    pageSize: 8
  };

  public columnsToAutoFit = ['crawfordRef',
    'policyholderPostcode',
    'lossPostcode',
    'insurerRef',
    'policyNumber',
    'lossDate',
    'instructionDate',
    'status'];

  public wrapSettings: TextWrapSettingsModel = { wrapMode: 'Both' };

  SEARCH_REGEX = /^[A-Za-z\d\s,\\\/\-_']+$/;
  searchLabel = 'Please enter search criteria';
  searchDesc = 'Searching on Crawford Reference, Insurer Reference (Claim Number) and Policy Number';
  patternDesc = 'Only alphanumeric characters and some special characters (, \\ / - \' _) are allowed';

  linkedFiles: GridModel = {
    queryString: 'crawfordRef',
    dataSource: this.linkedFilesData,
    detailDataBound: this.onDetailDataBound,
    rowSelected: this.showDetails.bind(this),
    allowPaging: true,
    columns: [
      { field: 'linkedFileRef', headerText: 'Linked File Reference' },
      { field: 'insurerRef', headerText: 'Insurer Reference' },
      { field: 'status', headerText: 'Status' },
      { field: 'serviceLine', headerText: 'Service Line' },
      { field: 'crawfordRef', headerText: 'Crawford Reference', visible: false }
    ]
  };

  private pageTitle: IPageTitle = {
    title: 'Claim Search'
  };

  private redirect = true;
  public userDetails: UserDetails;
  private element: HTMLElement;
  private scrollBehaviour: ScrollBehavior = 'smooth';

  public accordionIconClass = 'fa fa-lg fa-filter';

  constructor(
    private router: Router,
    private claimService: ClaimService,
    private searchFilterService: SearchFilterService,
    private userService: UserService,
    private snackbarService: CrawfordSnackbarService,
    private spinnerService: CrawfordSpinnerService,
    private downloadService: CrawfordDownloadService,
    private savedSearchService: SavedSearchService,
    private accordionService: AccordionService
  ) {
    this.spinnerService.startPageSpinner();
    this.formBuilder = new FormBuilder();
    this.advancedSearchGroup = this.formBuilder.group({});
    this.simpleSearchGroup = this.formBuilder.group({});
    this.simpleSearchOperatorsConfig = new SearchOperatorsConfig(this.searchFilterService.searchOperators, 0);
    this.advSearchOperatorsConfig = new SearchOperatorsConfig(this.searchFilterService.searchOperators, 2);
  }

  ngOnInit() {
    this.showGrid = false;
    this.adaptor = this.initializeAdaptor();
    this.userDetails = this.userService.userDetails;

    Observable.zip(
      this.statusSearch.controlsReady,
      this.insurerSearch.controlsReady,
      this.claimRefSearch.controlsReady
    ).subscribe(
      (allControlsReady) => {
        if (allControlsReady.every(ready => ready === true)) {
          this.restoreSavedSearch();
        }
      });

    this.element = this.gridStart.nativeElement;

    this.accordionChanges = {
      claimDataSource: false,
      claimRefSearch: false,
      nameSearch: false,
      insurerSearch: false,
      statusSearch: false,
      handlerSearch: false,
      locationSearch: false,
      dateSearch: false
    };

    if (!this.sourceSearch === null) {
      this.sourceSearch.claimDataSourceChanged.subscribe(
        value => {
          this.onClaimDataSourceChanged(value);
        }
      );
    }
  }

  private onClaimDataSourceChanged(claimDataSource: string): void {
    this.spinnerService.startPageSpinner();
    this.insurerSearch.loadFilterValues(claimDataSource);
    this.statusSearch.loadFilterValues(claimDataSource);
    this.handlerSearch.loadFilterValues(claimDataSource);

    Observable.zip(
      this.statusSearch.controlsReady,
      this.insurerSearch.controlsReady,
    ).subscribe(allControlsReady => {
      if (allControlsReady.every(ready => ready === true)) {
        this.spinnerService.stopPageSpinner();
      }
    });
  }

  export() {
    if (!this.showExportButton) {
      return;
    }

    this.router.navigate(['claim/search/export/']);
  }

  onAdvancedSearch(redirect?: boolean): void {
    this.redirect = redirect;
    this.adaptor.setAdditionalParameters(this.claimService.mapFormValues(this.advancedSearchGroup.value));
    this.claimService.saveSearch(new AdvancedSearch(this.advancedSearchGroup.getRawValue()));
    this.search({
      search: ''
    });
  }

  onAdvancedSearchKeyDown(event: KeyboardEvent) {
    if (event.keyCode === 13) {
      this.onAdvancedSearch(true);
      event.preventDefault();
    }
  }

  restoreSavedSearch() {
    this.defaultAdvancedSearch = this.advancedSearchGroup.value;
    const savedSearch: SimpleSearch | AdvancedSearch = this.claimService.loadSearch();
    if (savedSearch) {
      this.processSavedSearchRestore(savedSearch);
      if (savedSearch instanceof AdvancedSearch) {
        // Restoring status search separtly is required, since inner
        // controls are serially related to each other
        this.statusSearch.restoreStatusSearch(savedSearch.data);
        this.onAdvancedSearch(false);
        this.refSearchTextChange(savedSearch.data);
        this.clientAccountSearchTextChange(savedSearch.data);
      } else if (savedSearch instanceof SimpleSearch) {
        this.simpleSearch(savedSearch.data, false);
        this.simpleSearchTextChange(savedSearch.data);
      }
    }
    this.spinnerService.stopPageSpinner();
  }
  /* Restore the values in the advancedSearchGroup/simpleSearchGroup
     for the only controls present in the group
   */
  private processSavedSearchRestore(savedSearch) {
    let formGroup;
    if (savedSearch instanceof AdvancedSearch) {
      formGroup = this.advancedSearchGroup;
    } else if (savedSearch instanceof SimpleSearch) {
      formGroup = this.simpleSearchGroup;
    }
    const controls = formGroup.controls;
    Object.keys(controls).forEach(key => {
      controls[key].setValue(savedSearch.data[key]);
    });
  }

  simpleSearch(searchParams: any, redirect?: boolean): void {
    this.redirect = redirect;
    this.adaptor.setAdditionalParameters(null);
    this.claimService.saveSearch(new SimpleSearch(this.simpleSearchGroup.getRawValue()));
    this.search(searchParams);
  }

  private search(searchParams: any): void {
    this.setDefaultSearchOperator(searchParams);
    this.adaptor.setSearchingParameters(searchParams);
    this.linkedFilesData = new Array<AssociatedClaim>();
    this.crawfordRefs = new Array<string>();

    if (!this.advancedSearchGroup.value.includeLinked) {
      this.ccoGrid.childGrid.dataSource = null;
    } else {
      this.ccoGrid.childGrid.dataSource = this.linkedFilesData;
    }

    if (!this.dataSource) {
      this.spinnerService.startPageSpinner();
      this.loadgridData(this.claimService.getClaimSearchDataManager(this.adaptor)).then(() => {
        this.spinnerService.stopPageSpinner();
      });
    } else {
      this.ccoGrid.search(searchParams.search);
    }
    this.showGrid = true;
    this.element.scrollIntoView({ behavior: this.scrollBehaviour });
  }

  private setDefaultSearchOperator(searchParams: any) {
    if (!searchParams.searchOperator) {
      searchParams.searchOperator = '0';
    }
  }

  public onActionComplete(event: any): void {
    const hasData = event.rows && event.rows.length > 0;
    const isAdvancedSearch = this.claimService.loadSearch() instanceof AdvancedSearch;

    this.showExportButton = hasData && isAdvancedSearch;
    this.element.scrollIntoView({ behavior: this.scrollBehaviour });
  }

  private initializeAdaptor(): CrawfordApiGridAdaptor {
    const parameters = new ApiGridParameters();
    parameters.top = this.gridPageSettings.pageSize;
    parameters.orderBy.columnName = this.sortingSettings.columns[0].field;
    parameters.orderBy.direction = this.sortingSettings.columns[0].direction;

    return new CrawfordApiGridAdaptor(parameters);
  }

  resetSortingSettings() {
    if (this.ccoGrid) {
      this.ccoGrid.clearSorting();
    }
    this.sortingSettings = {
      columns: [{ field: 'instructionDate', direction: 'descending' }]
    };
  }

  showDetails($event: any) {
    if ($event) {
      if ($event.data.linkedFileRef) {
        this.showClaimDetails($event.data.linkedFileRef);
      } else {
        this.showClaimDetails($event.data.crawfordRef);
      }
    }
  }

  showClaimDetails(crawfordRef: string) {
    const userAccessErrorMessage = this.userDetails === 'Internal' ?
      'You do not have access to this extra secure claim' : 'You do not have access to this claim';

    if (crawfordRef) {
      let userHasAccess = false;
      this.claimService.checkUserHasAccess(crawfordRef).subscribe(access => {
        userHasAccess = access;
        if (userHasAccess) {
          this.loadClaimDetails(crawfordRef);
        } else {
          this.snackbarService.displayErrorMessage(userAccessErrorMessage);
        }
      });
    }
  }

  loadClaimDetails(crawfordReference: string): void {
    const source = this.advancedSearchGroup.value['claimDataSource'];
    this.accordionService.resetChanges();

    if (source) {
      this.router.navigate(['claim/search/', source, crawfordReference]);
    } else {
      this.router.navigate(['claim/search/', crawfordReference]);
    }
  }

  onActionFailure($event: any): void {
    this.snackbarService.displayErrorMessage('An error occurred during the search');
  }

  loadChildData(): Promise<boolean> {
    const promise: Promise<boolean> = new Promise((resolve, reject) => {
      this.spinnerService.startPageSpinner();
      if (this.advancedSearchGroup.value.includeLinked) {
        const pagedQuery = {
          orderBy: {
            columnName: this.sortingSettings.columns[0].field,
            direction: this.sortingSettings.columns[0].direction
          },
          top: this.gridPageSettings.pageSize,
          skip: 0,
          additional: this.claimService.mapFormValues(this.advancedSearchGroup.value),
          fullResults: true
        };

        pagedQuery.additional.crawfordRefs = this.crawfordRefs;

        this.claimService.getAssociatedClaimsForSearch(pagedQuery).subscribe(linkedFiles => {
          this.linkedFilesData = linkedFiles.result;
          this.ccoGrid.childGrid.dataSource = this.linkedFilesData;
          resolve(true);
        });
      }
    });

    return promise;
  }

  onDataBound($event: any): void {
    this.crawfordRefs = new Array<string>();
    this.linkedFilesData = new Array<AssociatedClaim>();

    if ($event.totalRecordCount === 1 && this.redirect) {
      const crawfordRef = $event.data[0].crawfordRef;
      this.claimService.checkUserHasAccess(crawfordRef).subscribe(access => {
        const userHasAccess = access;
        if (userHasAccess) {
          if (crawfordRef) {
            this.loadClaimDetails(crawfordRef);
          } else {
            this.snackbarService.displayErrorMessage('You do not have access to this extra secure claim');
          }
        }
      });
    } else {
      if (this.crawfordRefs.length === 0) {
        if ($event.data.length > 0) {
          this.crawfordRefs = $event.data.map(c => c.crawfordRef);

          if (this.advancedSearchGroup.value.includeLinked) {
            if (this.linkedFilesData.length > 0) {
              this.setChildFlag(true);
            } else {
              this.loadChildData().then(() => {
                this.spinnerService.stopPageSpinner();
                this.setChildFlag(true);
              });
            }
          }
        }
      }
    }
  }

  checkHasChildren(parentID: string): boolean {
    return this.linkedFilesData.find(p => p.crawfordRef === parentID) ? true : false;
  }

  setDefaultChildFlag($event) {
    this.ccoGrid.formatNoChildParentRow($event);
  }

  setChildFlag(linkedFiles: boolean) {
    if (linkedFiles) {
      this.crawfordRefs.forEach(parentId => {
        if (this.checkHasChildren(parentId)) {
          this.ccoGrid.formatChildParentRow(this.rowDetails.get(parentId));
        }
      });
    }
  }

  onReset(): void {
    this.claimRefSearch.resetSearchTypeDropdown();
    this.insurerSearch.resetSearchOperatorControl();
    this.savedSearchService.setRetrievedSearch(null);
    this.advancedSearchGroup.setValue(this.defaultAdvancedSearch);
    this.dataSource = null;
    this.showExportButton = false;
    this.accordionService.resetChanges();
  }

  onDetailDataBound($event): void {
  }

  onRowDataBound($event): void {
    const row = $event.row;

    if (this.crawfordRefs.length > 0) {
      row.classList.add('selectable-row');
    }

    this.rowDetails.set($event.data.crawfordRef, $event);
    this.setDefaultChildFlag($event);
  }

  private clientAccountSearchTextChange(event: any): void {
    const dropdown = this.advancedSearchGroup.get('clientAccountSearchOperator');
    this.searchTextChange(event.clientAccount, dropdown);
  }

  private refSearchTextChange(event: any): void {
    const dropdown = this.advancedSearchGroup.get('referenceSearchOperator');
    this.searchTextChange(event.referenceSearch, dropdown);
  }

  public simpleSearchTextChange(event: any): void {
    const dropdown = this.simpleSearchGroup.get('searchOperator');
    this.searchTextChange(event.search, dropdown);
  }

  searchTextChange(value: string, operatorDropdown: AbstractControl) {
    if (operatorDropdown) {
      if (value && value.indexOf(',') > -1) {
        operatorDropdown.disable();
        operatorDropdown.setValue(0);
      } else {
        operatorDropdown.enable();
      }
    }
  }

  retrieveSavedSearch() {
    this.router.navigate(['claim/search/retrieve-search']);
  }

  saveSearch() {
    let searchData: AdvancedSearch;
    searchData = this.advancedSearchGroup.getRawValue();

    this.saveSearchDialog.openDialog(searchData);
  }
  loadgridData(data: DataManager): Promise<boolean> {
    const promise: Promise<boolean> = new Promise((resolve, reject) => {
      if (data.dateParse) {
        setTimeout(() => {
          this.dataSource = data;
          resolve(true);
        }, 5000);
      } else {
        reject('Unable to connect server');
      }
    });
    return promise;
  }

  updateAccordionChanges(changes: any) {
    const changesKeys = Object.keys(changes);
    const accordionChangesKeys = Object.keys(this.accordionChanges);
    let rootAccordionHasChanges = false;

    for (let j = 0; j < accordionChangesKeys.length; j++) {
      const accordionKey = accordionChangesKeys[j];
      let accordionHasChanges = false;

      for (let i = 0; i < changesKeys.length; i++) {
        const changesKey = changesKeys[i];

        if (changes[changesKey] && changesKey.startsWith(accordionKey)) {
          rootAccordionHasChanges = true;
          accordionHasChanges = true;
          break;
        }
      }
      this.accordionChanges[accordionKey] = accordionHasChanges;
    }

    // this.accordionChanges = changes;
    this.rootAccordionHasChanges = rootAccordionHasChanges;

  }

  ngAfterViewInit(){
    this.accordionService.onAccordionValueChange((changes) => this.updateAccordionChanges(changes));
  }

}



// WEBPACK FOOTER //
// D:/a/1/s/UI/src/app/claim/claim-list/claim-list.component.ts